import { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import * as orgService from '../services/organization'
import { getOrganizationSubscription } from '../services/featureGating'
import { isDemoOrganization } from '../services/demoData'

const OrganizationContext = createContext(null)

export const useOrganization = () => {
  const context = useContext(OrganizationContext)
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider')
  }
  return context
}

export const OrganizationProvider = ({ children }) => {
  const [organization, setOrganization] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [permissions, setPermissions] = useState({})
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)

  const loadOrganization = async () => {
    try {
      setLoading(true)
      const org = await orgService.getActiveOrganization()
      if (!org) {
        setOrganization(null)
        setUserRole(null)
        setPermissions({})
        setSubscription(null)
        return
      }

      setOrganization(org)
      const roleData = await orgService.getUserRole(org.id)
      if (roleData) {
        setUserRole(roleData.role)
        setPermissions(roleData.permissions || {})
      }

      // Get subscription info (try both old and new subscription tables)
      let sub = await orgService.getSubscription(org.id)
      if (!sub) {
        // Try new subscription system
        sub = await getOrganizationSubscription(org.id)
      }
      setSubscription(sub)
    } catch (error) {
      console.error('Error loading organization:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadOrganization()

    // Reload when auth state changes, but debounce to prevent excessive reloads
    let reloadTimeout
    let lastUserId = null
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Only reload on actual auth changes (sign in/out), NOT on token refresh
      // This prevents reloads when Supabase refreshes tokens on visibility change
      // Also check if this is a duplicate SIGNED_IN for the same user (session restoration)
      // Note: App.jsx also handles duplicate detection, this is a secondary check
      const currentUserId = session?.user?.id
      const isDuplicateSignIn = event === 'SIGNED_IN' && lastUserId === currentUserId
      
      if (event === 'SIGNED_OUT') {
        lastUserId = null
        // Clear any pending reload
        if (reloadTimeout) {
          clearTimeout(reloadTimeout)
        }
        // Debounce the reload to prevent rapid-fire reloads
        reloadTimeout = setTimeout(() => {
          loadOrganization()
        }, 500)
      } else if (event === 'SIGNED_IN' && !isDuplicateSignIn) {
        // Only reload if this is a NEW sign-in (different user or first time)
        lastUserId = currentUserId
        // Clear any pending reload
        if (reloadTimeout) {
          clearTimeout(reloadTimeout)
        }
        // Debounce the reload to prevent rapid-fire reloads
        reloadTimeout = setTimeout(() => {
          loadOrganization()
        }, 500)
      } else if (isDuplicateSignIn) {
        // Log that we're ignoring a duplicate SIGNED_IN
        console.debug('[OrganizationContext] Ignoring duplicate SIGNED_IN event for existing session')
      }
      // Explicitly ignore TOKEN_REFRESHED - don't reload on token refresh
    })

    return () => {
      if (reloadTimeout) {
        clearTimeout(reloadTimeout)
      }
      subscription.unsubscribe()
    }
  }, [])

  const checkPermission = async (resource, action = 'read') => {
    if (!organization) return false
    return await orgService.hasPermission(organization.id, resource, action)
  }

  const isAdmin = userRole === 'admin'
  const isPractitioner = userRole === 'practitioner' || isAdmin
  const canWrite = (resource) => {
    if (isAdmin) return true
    return permissions[resource] === 'write' || isPractitioner
  }
  const canRead = (resource) => {
    if (isAdmin || isPractitioner) return true
    return permissions[resource] === 'read' || permissions[resource] === 'write'
  }

  const value = {
    organization,
    userRole,
    permissions,
    subscription,
    loading,
    isAdmin,
    isPractitioner,
    canWrite,
    canRead,
    checkPermission,
    reload: loadOrganization,
  }

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  )
}

